import { Close, Search } from '@mui/icons-material';
import {
  Box,
  IconButton,
  InputAdornment,
  Link,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import { RichTreeViewPro } from '@mui/x-tree-view-pro/RichTreeViewPro';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  athenaApi,
  HandlersLibraryTreeNode,
  useGetApiLibraryTreeQuery,
  usePostApiLibraryLabelByIdAndNewLabelMutation,
  usePostApiLibraryMoveByIdAndNewParentIdMutation,
} from '../../../../app/athenaApiGen';
import {
  closeInfoBar,
  setInfoBarDetail,
  setPendingCreateItemId,
  setSelectedItem,
} from '../../../../app/library/librarySlice';
import { AppDispatch, RootState, store } from '../../../../app/store';
import EmptyTree from '../EmptyTree';
import TreeSkeleton from '../TreeSkeleton';
import CustomTreeItem from './components/CustomTreeItem';
import {
  LibraryTreeProvider,
  type LibraryTreeContextValue,
} from './LibraryTreeContext';

type TDynamicTreeProps = {
  treeRef: React.RefObject<any>;
};

const DynamicTree = (props: TDynamicTreeProps) => {
  // Typed dispatch so RTK Query's util thunks (updateQueryData, etc.)
  // return their proper PatchCollection result instead of being treated
  // as UnknownAction. Required for the optimistic-rename rollback pattern
  // below: `patchTree.undo()` only typechecks with thunk-aware dispatch.
  const dispatch = useDispatch<AppDispatch>();
  const { selectedItem, infoBar, allowedActions } = useSelector(
    (state: RootState) => state.library,
  );

  const {
    data,
    isFetching: isTreeFetching,
    isLoading: isTreeLoading,
  } = useGetApiLibraryTreeQuery({
    includeAll: false,
  });
  const [updateLabel] = usePostApiLibraryLabelByIdAndNewLabelMutation();
  const [moveItem] = usePostApiLibraryMoveByIdAndNewParentIdMutation();

  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  // Search state: raw input + 150ms-debounced value used by the filter.
  // Filtering on every keystroke feels jumpy for fast typers.
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 150);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Filter the tree by case-insensitive substring match on label. A node
  // survives if it matches OR any descendant matches; we keep ancestor IDs
  // so we can force-expand the matched paths.
  const { filteredData, searchAncestors } = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) {
      return { filteredData: data, searchAncestors: null as string[] | null };
    }
    const ancestors = new Set<string>();
    const walk = (
      nodes: HandlersLibraryTreeNode[],
    ): HandlersLibraryTreeNode[] => {
      const out: HandlersLibraryTreeNode[] = [];
      for (const node of nodes) {
        const selfMatches = node.label?.toLowerCase().includes(q) ?? false;
        const filteredChildren = node.children?.length
          ? walk(node.children)
          : [];
        if (selfMatches || filteredChildren.length > 0) {
          out.push({ ...node, children: filteredChildren });
          if (filteredChildren.length > 0 && node.id) {
            ancestors.add(node.id);
          }
        }
      }
      return out;
    };
    return {
      filteredData: walk(data ?? []),
      searchAncestors: Array.from(ancestors),
    };
  }, [data, debouncedSearch]);

  const isSearchActive = (debouncedSearch.trim().length ?? 0) > 0;
  // During search, force-expand only the ancestor paths of matches and
  // ignore user toggles (they can't visually collapse a match path anyway).
  // When search clears, the tree returns to the user's prior expansion.
  const effectiveExpandedItems = isSearchActive
    ? (searchAncestors ?? [])
    : expandedItems;

  const expandItem = (itemId: string) =>
    setExpandedItems((prev) =>
      prev.includes(itemId) ? prev : [...prev, itemId],
    );

  // Toggle expansion on click: collapse if already expanded, else expand.
  const toggleItem = (itemId: string) =>
    setExpandedItems((prev) =>
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId],
    );

  const handleExpandedItemsChange = (
    _event: React.SyntheticEvent | null,
    itemIds: string[],
  ) => {
    // During a search the tree's expansion is forced to search-ancestors;
    // user toggles would visually no-op, so ignore them rather than thrash
    // the saved user state.
    if (isSearchActive) return;
    setExpandedItems(itemIds);
  };

  // Sync row-select from the cached tree node. The tree query already
  // returns every field the ViewPanel uses (description, dateSubmitted,
  // deliverableType, children, etc.), so we don't need a follow-up
  // per-item fetch — one dispatch, one render. Per-deliverable extras
  // (dueDate, author, workflowState) come from a separate
  // useGetApiDeliverablesByIdQuery inside ViewPanel.
  const findInTree = (
    nodes: HandlersLibraryTreeNode[] | undefined,
    targetId: string,
  ): HandlersLibraryTreeNode | null => {
    if (!nodes) return null;
    for (const n of nodes) {
      if (n.id === targetId) return n;
      const found = findInTree(n.children, targetId);
      if (found) return found;
    }
    return null;
  };

  const handleTreeItemClick = (itemId: string | null) => {
    if (!itemId) return;
    const node = findInTree(data, itemId);
    if (node) dispatch(setSelectedItem(node));
  };

  useEffect(() => {
    if (!data) return;
    if (selectedItem?.id) {
      // On remount, restore expansion for the already-selected item
      // (expandedItems is local state and resets on navigation).
      expandItem(selectedItem.id);
    } else {
      const first = data?.[0];
      dispatch(setSelectedItem(first || null));
      // Expand the default-selected root.
      if (first?.id) expandItem(first.id);
    }
  }, [isTreeLoading]);

  // Re-sync the selected node from the freshly-fetched tree whenever the tree
  // data changes (e.g. after a create/delete/rename invalidation refetch).
  // selectedItem is a detached snapshot captured at click time; without this
  // the ViewPanel child count stays stale ("This folder is empty" after adding
  // a child) and the delete cascade walks stale children and misses
  // just-created ones (issue #1333). Only re-sync when the node still exists;
  // deletion of the selected item clears selection through its own handler.
  useEffect(() => {
    if (!data || !selectedItem?.id) return;
    const fresh = findInTree(data, selectedItem.id);
    if (fresh && fresh !== selectedItem) {
      dispatch(setSelectedItem(fresh));
    }
  }, [data]);

  const isReorderingEnabled = !!allowedActions.find(({ action }) =>
    action?.includes('add-deliverable-to-directory'),
  );

  // Intentionally not mirroring isFetching → isItemLoading. selectedItem now
  // updates optimistically from the tree node on click, so ViewPanel no
  // longer needs to gate on a loading flag during row navigation.

  // Tree-wide data exposed to all CustomLabel instances via context, so
  // children don't have to subscribe to useGetApiLibraryTreeQuery each. The
  // Set + value reference are memoised on the tree data so consumers don't
  // re-render except when the underlying tree actually changes.
  const libraryTreeContextValue = useMemo<LibraryTreeContextValue>(() => {
    const rootIds = new Set<string>();
    const entityTypes = new Map<string, string>();
    const collect = (nodes: HandlersLibraryTreeNode[]) => {
      for (const node of nodes) {
        if (node.id && node.entityType) entityTypes.set(node.id, node.entityType);
        if (node.children?.length) collect(node.children);
      }
    };
    for (const node of data ?? []) {
      if (node.id) rootIds.add(node.id);
    }
    collect(data ?? []);
    return { rootItemIds: rootIds, entityTypesById: entityTypes };
  }, [data]);

  return (
    <LibraryTreeProvider value={libraryTreeContextValue}>
    <React.Fragment>
      {(data?.length ?? 0) > 0 ? (
        <Box
          sx={{
            width: '40%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          {/* Search input — case-insensitive substring on label, debounced
              150ms. Filters the tree client-side; force-expands ancestors
              of matches. */}
          <TextField
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            fullWidth
            placeholder="Search library…"
            // mr matches the visual offset of the kebab IconButton from the
            // right edge of the tree column, so the search field's right
            // edge sits in line with the kebab dots below it.
            sx={{ mb: 1, mr: 4 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label="Clear search"
                      onClick={() => setSearchTerm('')}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              },
            }}
          />
          {isSearchActive && (filteredData?.length ?? 0) === 0 ? (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.secondary',
                px: 2,
                textAlign: 'center',
              }}
            >
              <Typography variant="body2">
                No matches for “{debouncedSearch.trim()}”.{' '}
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  onClick={() => setSearchTerm('')}
                  sx={{
                    verticalAlign: 'baseline',
                    // Kill the MUI button-base hover background that was
                    // painting a blocky blue rectangle behind the link.
                    '&:hover': { backgroundColor: 'transparent' },
                    '&:focus': { backgroundColor: 'transparent' },
                  }}
                >
                  Clear search
                </Link>
              </Typography>
            </Box>
          ) : (
        <RichTreeViewPro
          sx={(theme) => ({
            width: '100%',
            flex: 1,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            '.MuiTreeItem-iconContainer': {
             
            },
            // Hover state — no background when not selected
            '& .MuiTreeItem-content:hover': {
              backgroundColor: 'transparent',
              cursor: 'pointer',
            },
            // Selected/Active state — light gray, matching the left-nav selection
            '& .MuiTreeItem-content.Mui-selected': {
              backgroundColor: theme.palette.action.selected,
              '&:hover': {
                backgroundColor: theme.palette.action.selected,
              },
            },
            // Remove focus outlines
            '& .MuiTreeItem-content:focus, & .MuiTreeItem-content:focus-within': {
              outline: 'none',
            },
            '& .MuiTreeItem-content.Mui-focused': {
              outline: 'none',
              backgroundColor: 'transparent',
            },
            '& .MuiTreeItem-content.Mui-selected.Mui-focused': {
              backgroundColor: theme.palette.action.selected,
            },
          })}
          apiRef={props.treeRef}
          items={filteredData ?? []}
          itemsReordering={isReorderingEnabled && !isSearchActive}
          isItemEditable={!isSearchActive}
          slots={{
            item: CustomTreeItem,
          }}
          selectedItems={selectedItem ? selectedItem.id : null}
          onSelectedItemsChange={(_, itemId) => handleTreeItemClick(itemId)}
          onItemClick={(event, itemId) => {
            // Chevron click already toggles expansion via
            // onExpandedItemsChange; skip our manual toggle so the
            // two don't cancel out into a no-op. Kebab clicks also
            // skip toggle — opening the 3-dot menu shouldn't
            // expand/collapse the row.
            const target = event?.target as HTMLElement | null;
            if (target?.closest?.('.MuiTreeItem-iconContainer')) return;
            if (target?.closest?.('.library-kebab-trigger')) return;
            toggleItem(itemId);
          }}
          getItemId={(item) => item.id ?? ''}
          expandedItems={effectiveExpandedItems}
          onExpandedItemsChange={handleExpandedItemsChange}
          canMoveItemToNewPosition={(params) => {
            // The backend rejects moving BOTH projects and programs:
            // MoveLibraryItem returns 400 "Projects cannot be moved between
            // programs" and "Programs are root-level entities and cannot be
            // moved" — and there is no same-program project reorder either. So
            // block any project/program drag up front: the user gets no-drop
            // feedback instead of a failed request + raw error. Directories,
            // deliverables, and attachments are unaffected.
            // NOTE: the data model technically allows reparenting a project
            // (program_id is a mutable FK) and PATCH /api/projects will do it
            // unguarded — that policy gap is tracked in #1413; this guard
            // mirrors the move-endpoint behavior, which is the UI contract.
            const moving = findInTree(data, params.itemId);
            return (
              moving?.entityType !== 'project' &&
              moving?.entityType !== 'program'
            );
          }}
          onItemPositionChange={async (params) => {
            try {
              await moveItem({
                id: params.itemId,
                newParentId: params.newPosition.parentId ?? '',
              }).unwrap();
              // The move mutation invalidates the 'Library' tag, so the tree
              // refetches and settles into the new order on its own.
            } catch (err: FetchBaseQueryError | any) {
              // Surface the backend's own detail (project/program moves are
              // already blocked upstream by canMoveItemToNewPosition, so this
              // path is for genuine server-side rejections of otherwise-valid
              // moves). Then refetch so the optimistically-reordered row snaps
              // back to its original position.
              const e = err as
                | { data?: { details?: string; message?: string; error?: string } }
                | undefined;
              dispatch(
                setInfoBarDetail(
                  e?.data?.details ??
                    e?.data?.message ??
                    e?.data?.error ??
                    'Move failed. Please try again.',
                ),
              );
              dispatch(athenaApi.util.invalidateTags(['Library']));
            }
          }}
          onItemLabelChange={async (itemId, newLabel) => {
            // Optimistic UI: update the tree cache + selectedItem label
            // immediately so the renamed item visibly settles without
            // waiting on the backend round-trip + Library-tag refetch.
            // RTK Query's util.updateQueryData returns a ThunkAction whose
            // PatchCollection result is what we need to roll back on failure.
            // The codegen-api thunk's typed RootState doesn't always line up
            // with the consuming store's typed RootState narrowly enough for
            // tsc (a known RTK Query typing gap), so we narrow the result to
            // the patch shape we actually use. Runtime behaviour is unchanged.
            const patchTree = store.dispatch(
              athenaApi.util.updateQueryData(
                'getApiLibraryTree',
                { includeAll: false },
                (draft) => {
                  const node = findInTree(draft, itemId);
                  if (node) node.label = newLabel;
                },
              ) as unknown as Parameters<typeof store.dispatch>[0],
            ) as unknown as { undo: () => void };
            const previousSelected = selectedItem;
            if (selectedItem?.id === itemId) {
              dispatch(
                setSelectedItem({ ...selectedItem, label: newLabel }),
              );
            }

            try {
              const data = await updateLabel({
                id: itemId,
                newLabel,
              }).unwrap();
              // Cover all surfaces that may show the renamed item:
              // - Library tree (auto-invalidated via the mutation's own
              //   invalidatesTags: ['Library'], but Projects/Deliverables
              //   tagged queries may show the same project/deliverable
              //   under a different shape and need a kick too).
              store.dispatch(
                athenaApi.util.invalidateTags([
                  'Deliverables',
                  'Projects',
                ]),
              );
              if (data) {
                // Trust the optimistic label over the BE response — the
                // BE sometimes returns a stale record on this endpoint
                // (rename takes multiple refreshes to settle), and the
                // user's typed name is what should win until BE catches
                // up. Other fields from the response are still useful
                // for timestamps / metadata.
                dispatch(setSelectedItem({ ...data, label: newLabel }));
              }
              // Folder is now a "real" named folder — clear the pending-
              // create flag so a later cancel-during-rename doesn't try
              // to roll back what's now an intentional folder.
              dispatch(setPendingCreateItemId(null));
            } catch (err: FetchBaseQueryError | any) {
              // Roll back optimistic updates on failure.
              patchTree.undo();
              if (previousSelected) {
                dispatch(setSelectedItem(previousSelected));
              }
              err?.data &&
                dispatch(
                  setInfoBarDetail(
                    err.data.details ?? 'Rename failed. Please try again.',
                  ),
                );
            }
          }}
        />
          )}
        </Box>
      ) : isTreeFetching || isTreeLoading ? (
        <TreeSkeleton />
      ) : (
        <EmptyTree />
      )}
      <Snackbar
        open={infoBar.isOpen}
        autoHideDuration={6000}
        onClose={() => dispatch(closeInfoBar())}
        message={infoBar.details}
      />
    </React.Fragment>
    </LibraryTreeProvider>
  );
};

export { DynamicTree };
